import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { CustomerToken, DataSourceLoadOptions } from '@sleed/bizcommerce-api';
import { customersApi, binaryRepoImagesApi } from '~/lib/api/client';
import type { SortDir } from '~/lib/grid/sort';
import { formToToken, type CustomerFormValues } from '~/lib/schemas/customer';

export interface CustomersListParams {
	skip?: number;
	take?: number;
	/** Free-text search (→ additionalFilteringCustomers.searchText: name / email / phone). */
	search?: string;
	sortSelector?: string | null;
	sortDir?: SortDir;
}

export interface CustomersListResult {
	items: CustomerToken[];
	totalCount: number;
}

export const customerKeys = {
	all: ['customers'] as const,
	list: (params: unknown) => ['customers', 'list', params] as const,
	detail: (id: number) => ['customers', 'detail', id] as const
};

/** Server-paged customers list via `getAllPost` (DevExtreme loadOptions envelope). */
export function useCustomersList(params: CustomersListParams = {}) {
	const { skip = 0, take = 25, search = '', sortSelector = null, sortDir = 'asc' } = params;
	return useQuery<CustomersListResult>({
		queryKey: customerKeys.list({ skip, take, search, sortSelector, sortDir }),
		queryFn: async () => {
			const loadOptions: DataSourceLoadOptions = {
				skip,
				take,
				requireTotalCount: true,
				...(sortSelector ? { sort: [{ selector: sortSelector, desc: sortDir === 'desc' }] } : {})
			};
			const res = (
				await customersApi.getAllPost({
					loadOptions,
					additionalFilteringCustomers: search ? { searchText: search } : {}
				})
			).data;
			return { items: res.data ?? [], totalCount: res.totalCount ?? 0 };
		},
		placeholderData: keepPreviousData
	});
}

/** Single customer by id (full record). `create` → null. */
export function useCustomer(id: number | null) {
	return useQuery<CustomerToken | null>({
		queryKey: customerKeys.detail(id ?? 0),
		queryFn: async () => (id && id > 0 ? (await customersApi.getById(id)).data : null),
		enabled: id != null && id > 0
	});
}

export function useCustomerMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: customerKeys.all });
	const create = useMutation({
		// The controller returns Ok(created) with the new id, though the generated client types it `void`.
		mutationFn: async (form: CustomerFormValues) => (await customersApi.insert(formToToken(form))).data as unknown as CustomerToken,
		onSuccess: invalidate
	});
	const update = useMutation({
		// Spread the original token first so server-owned fields not on the form survive the round-trip.
		mutationFn: async ({ form, original }: { form: CustomerFormValues; original: CustomerToken | null }) =>
			(await customersApi.update({ ...(original ?? {}), ...formToToken(form) })).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await customersApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}

/**
 * Upload a customer image into slot `key` ("1"). The customer must already exist (id > 0); the
 * File is attached as form-data keyed by the slot subkey (mirrors the legacy
 * BinaryRepoImages/UploadCustomers contract and the manufacturer/status uploads).
 */
export async function uploadCustomerImage(customerId: number, key: '1', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadCustomers(customerId, key, { data: fd });
}

/** Open a storefront impersonation session for a customer (returns a redirect URL). */
export function useImpersonateCustomer() {
	return useMutation({
		mutationFn: async (customerId: number) => (await customersApi.createCustomerImpersonation(customerId)).data
	});
}
